import { Controller } from "@hotwired/stimulus";

const NAME_FIELD = "character_class_name";

// The class form's restrictions card: the alignments, with the line that says
// what the Oficina will do, and the race checklist. Every sentence
// comes from the server (phrases value). "Todas as raças" hides the race
// checklist; the server drops the races it sent (CharacterClassFormParams).
export default class extends Controller<HTMLElement> {
	static targets = ["alignment", "alignmentEffect", "modeOnly", "racesSection", "race", "option", "chips", "chip", "count", "filter", "error"];
	static values = { phrases: Object, connectors: Object };

	declare readonly alignmentTargets: HTMLInputElement[];
	declare readonly alignmentEffectTarget: HTMLElement;
	declare readonly modeOnlyTarget: HTMLInputElement;
	declare readonly racesSectionTarget: HTMLElement;
	declare readonly raceTargets: HTMLInputElement[];
	declare readonly optionTargets: HTMLElement[];
	declare readonly chipsTarget: HTMLElement;
	declare readonly chipTarget: HTMLTemplateElement;
	declare readonly countTarget: HTMLElement;
	declare readonly filterTarget: HTMLInputElement;
	declare readonly errorTarget: HTMLElement;
	declare readonly phrasesValue: Record<string, string>;
	declare readonly connectorsValue: Record<string, string>;

	private readonly onName = () => this.update();

	connect() {
		document.getElementById(NAME_FIELD)?.addEventListener("input", this.onName);
		this.update();
	}

	disconnect() {
		document.getElementById(NAME_FIELD)?.removeEventListener("input", this.onName);
	}

	update() {
		const only = this.modeOnlyTarget.checked;
		this.racesSectionTarget.classList.toggle("hidden", !only);

		this.describeAlignments(this.className());
		this.describeRaces(only);
	}

	filter() {
		const query = this.filterTarget.value.trim().toLowerCase();
		for (const option of this.optionTargets) option.classList.toggle("hidden", !(option.dataset.name ?? "").toLowerCase().includes(query));
	}

	remove(event: Event) {
		const slug = (event.currentTarget as HTMLElement).dataset.slug;
		const race = this.raceTargets.find((input) => input.value === slug);
		if (race) race.checked = false;
		this.update();
	}

	private className() {
		const typed = document.getElementById(NAME_FIELD) as HTMLInputElement | null;
		return typed?.value.trim() || this.say("class_fallback");
	}

	private say(key: string, vars: Record<string, string> = {}) {
		return (this.phrasesValue[key] ?? "").replace(/%\{(\w+)\}/g, (_, name) => vars[name] ?? "");
	}

	private show(target: HTMLElement, text: string, invalid: boolean) {
		target.textContent = text;
		target.dataset.invalid = String(invalid);
	}

	private describeAlignments(name: string) {
		const chosen = this.alignmentTargets.filter((input) => input.checked).map((input) => input.dataset.label ?? input.value);
		if (chosen.length === 0) return this.show(this.alignmentEffectTarget, this.say("alignment_none"), true);
		if (chosen.length === this.alignmentTargets.length) return this.show(this.alignmentEffectTarget, this.say("alignment_all"), false);

		const last = chosen[chosen.length - 1];
		const head = chosen.slice(0, -1);
		let list = last;
		if (head.length === 1) list = `${head[0]}${this.connectorsValue.two}${last}`;
		else if (head.length > 1) list = `${head.join(", ")}${this.connectorsValue.last}${last}`;
		const note = this.say("note", { class_name: name, list });
		this.show(this.alignmentEffectTarget, this.say("alignment_some", { note }), false);
	}

	private describeRaces(only: boolean) {
		const checked = this.raceTargets.filter((input) => input.checked);
		this.countTarget.textContent = String(checked.length);
		this.chipsTarget.replaceChildren(...checked.map((input) => this.chipFor(input)));

		this.errorTarget.textContent = only && checked.length === 0 ? this.say("races_error") : "";
	}

	private chipFor(input: HTMLInputElement) {
		const chip = (this.chipTarget.content.firstElementChild as HTMLElement).cloneNode(true) as HTMLElement;
		const label = input.dataset.label ?? input.value;
		(chip.querySelector("[data-chip-name]") as HTMLElement).textContent = label;
		const button = chip.querySelector("[data-chip-remove]") as HTMLButtonElement;
		button.dataset.slug = input.value;
		button.setAttribute("aria-label", this.say("remove", { name: label }));
		return chip;
	}
}
